import {
  type FC,
  useEffect,
  useRef,
  useContext,
  type ComponentPropsWithoutRef,
} from "react";
import classNames from "classnames";
import { IntlContext, type TranslationKey } from "../helpers/intlHooks";
import { FormattedMessage } from "react-intl";

interface FocusableHeaderProps
  extends Omit<ComponentPropsWithoutRef<"h1">, "children"> {
  text:
    | string
    | {
        id: TranslationKey;
        values: ComponentPropsWithoutRef<typeof FormattedMessage>["values"];
      };
  ignoreFocus?: boolean;
  scrollOnFocus?: boolean;
  srOnly?: boolean;
}

export const FocusableHeader: FC<FocusableHeaderProps> = ({
  className = "",
  id,
  ignoreFocus,
  scrollOnFocus = false,
  srOnly,
  text,
  ...headingProps
}) => {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const fm = useContext(IntlContext).formatMessage;
  const documentTitle =
    typeof text === "string" ? text : fm({ id: text.id }, text.values);

  useEffect(() => {
    if (!ignoreFocus) {
      headingRef.current?.focus({ preventScroll: !scrollOnFocus });
    }
  }, [headingRef, ignoreFocus, scrollOnFocus, text]);

  useEffect(() => {
    if (typeof documentTitle === "string") {
      document.title = documentTitle;
    } else if (headingRef.current?.textContent) {
      document.title = headingRef.current.textContent;
    }
  }, [documentTitle]);

  return (
    <h1
      ref={headingRef}
      className={classNames(className, {
        "ds-u-visibility--screen-reader": srOnly,
      })}
      tabIndex={-1}
      id={id}
      {...headingProps}
    >
      {typeof text === "string" ? (
        `${text}`
      ) : (
        <>
          <FormattedMessage id={text.id} values={text.values} />
        </>
      )}
    </h1>
  );
};
